// Features.tsx
import React from 'react';
import { InfoAlert } from '../components/InfoAlert';
import { FaCheck, FaTimes } from 'react-icons/fa'; // Importing necessary icons

interface Feature {
  name: string;
  free: boolean;
  pro: boolean;
  freeAlways?: boolean; // true = always free (no asterisk), false/undefined = free trial (14 days)
  description: string;
}

interface FeatureGroup {
  groupName: string;
  features: Feature[];
}

const Features: React.FC = () => {

  const featureGroups: FeatureGroup[] = [
    {
      groupName: 'General',
      features: [
        {
          name: 'Auto Connect',
          free: true,
          pro: true,
          description: 'Automatically connect to SAMMI when Deck Hopper launches.',
        },

        {
          name: 'Visual Deck List',
          free: true,
          pro: true,
          description: 'A clear and organized list of all your decks.',
        },
        {
          name: 'Optimized for touch and mouse',
          free: true,
          pro: true,
          description: 'Easily navigate between decks using swipe gestures (Android) or keyboard and mouse (Desktop).',
        },
        {
          name: 'Browser and OBS Dock Support',
          free: false,
          pro: true,
          description: 'Open Deck Hopper in any browser or OBS dock.',
        },
      ],
    },
    {
      groupName: 'Deck Management',
      features: [
        {
          name: 'Hide or Disable Decks',
          free: false,
          pro: true,
          description: 'Control the visibility and status of your decks. Hidden decks are still active.',
        },
        {
          name: 'Show and Hide Disabled or Hidden Decks',
          free: true,
          pro: true,
          description: 'Toggle the visibility of hidden or disabled decks in the deck list.',
        },
      ],
    },
    {
      groupName: 'Button Management',
      features: [
        {
          name: 'Interact with Buttons',
          free: true,
          pro: true,
          description: 'Interact with your buttons: press, hold, release.',
        },
        {
          name: 'View Blocked Buttons',
          free: true,
          pro: true,
          description: 'See which buttons are blocked within each deck.',
        },
        {
          name: 'Advanced Button Menus',
          free: false,
          pro: true,
          description: 'Stop, edit and block buttons, reinit default variables, reset SAMMI.',
        },
        {
          name: 'Button Customization',
          free: false,
          pro: true,
          description: 'Change button text color, font style, alignment and more.',
        },
        {
          name: 'Transparent Buttons',
          free: true,
          pro: true,
          description: 'Full support for transparent SAMMI buttons.',
        },
      ],
    },
    {
      groupName: 'Exclusive Features',
      features: [
        {
          name: 'Deck Inspector',
          free: true,
          pro: false,
          freeAlways: true,
          description: 'Load your SAMMI deck JSON files offline, filter buttons by deck name, triggers, commands, and more. Available on Windows.',
        },
        {
          name: 'Real Time Inspector',
          free: false,
          pro: true,
          description: 'View all buttons at a glance and filter by deck name, button text, triggers, and more.',
        },
         {
          name: 'Record Button Activity',
          free: false,
          pro: true,
          description: 'Track how many times each button has been triggered along with timestamps. Available on Windows and Android.',
        },
        {
          name: 'Proxy Buttons',
          free: false,
          pro: true,
          description: 'Create button shortcuts in your decks for quick access without duplicating buttons.',
        },

        {
          name: 'Twitch Embed Buttons',
          free: false,
          pro: true,
          description: 'Display your Twitch stream or chat alongside your buttons in Deck Hopper. Supports login. Not available on iOS.',
        },
      ],
    },
    {
      groupName: 'Interface',
      features: [
        {
          name: 'Persistent Navigation Bar',
          free: true,
          pro: true,
          description: 'Show navigation bar while inside a deck for easier navigation.',
        },
        {
          name: 'Multiple Windows',
          free: true,
          pro: true,
          description: 'Open multiple instances of Deck Hopper on your desktop, each with its own settings.',
        },
        {
          name: 'Haptic Feedback',
          free: true,
          pro: true,
          description: 'Receive haptic feedback when interacting with buttons on Android.',
        },
        {
          name: 'Disable Context Menu',
          free: true,
          pro: true,
          description: 'Completely disable the context menu on right click (for touch screens on Windows).',
        },
      ],
    },
    {
      groupName: 'Quality of Life',
      features: [
        {
          name: 'Always on Top',
          free: true,
          pro: true,
          description: 'Keep Deck Hopper always visible on your desktop.',
        },
        {
          name: 'Keep Screen Active',
          free: true,
          pro: true,
          description: 'Prevent your screen from sleeping while using Deck Hopper on Android.',
        },
        {
          name: 'Image Caching',
          free: true,
          pro: true,
          description: 'Automatic deck and button image caching for faster loading times.',
        },
        {
          name: 'Launch Flags',
          free: false,
          pro: true,
          description: 'Start Deck Hopper with specific settings or open particular decks automatically (Desktop).',
        },
      ],
    },
    {
      groupName: 'Integrations',
      features: [
        {
          name: 'Deck App: Wait For commands',
          free: false,
          pro: true,
          description: 'Displays user input popups within Deck Hopper, such as text input, options, and more.',
        },
        {
          name: 'Popup Message Command',
          free: false,
          pro: true,
          description: 'Displays a popup message within Deck Hopper. Supports auto close and Close Popup Message command.',
        },
        {
          name: 'Open URL Command',
          free: false,
          pro: true,
          description: 'Opens a custom URL within Deck Hopper. Supports auto close and Close URL command, as well as extra Desktop customizations.'
        },
        {
          name: 'Vibrate Command',
          free: true,
          pro: true,
          description: 'Vibrates your Android device.',
        },

      ],
    },
  ];

  return (
    <div className="flex flex-col items-center justify-center setup-guide bg-dark-blue text-gray-300 p-4 h-full w-[100em] pb-10">
      <h1 className="text-4xl text-center text-gray-300 mb-4 mt-4">Features</h1>
      <p className="text-xl text-center text-gray-400 mb-6">
        Explore all the available features in Deck Hopper.
      </p>

      <hr className="border-t border-gray-600 w-2/3 mb-8" />
      <InfoAlert message="The iOS version includes all Pro features immediately upon purchase—no extra unlocks needed." />
      <div className="text-center mt-8 justify-center items-center max-w-[70em] w-full">

        {/* Comparison Table */}
        <div className="overflow-x-auto border border-gray-700 rounded-xl shadow-lg">
          <table className="min-w-full bg-bgBorderBlue bg-opacity-30 rounded-lg shadow-lg">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="px-6 py-3 text-left text-lg font-semibold text-gray-300">Feature</th>
                <th className="px-6 py-3 text-center text-lg font-semibold text-gray-300">Free</th>
                <th className="px-6 py-3 text-center text-lg font-semibold text-gray-300">Basic</th>
                <th className="px-6 py-3 text-center text-lg font-semibold text-gray-300">Pro</th>
              </tr>
            </thead>
            <tbody>
              {featureGroups.map((group, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  {/* Group Header */}
                  <tr className={`${group.groupName === 'Exclusive Features' ? 'bg-purple-600/80' : 'bg-blue-600/80'} `}>
                    <td colSpan={4} className="px-6 py-3 text-left text-lg font-bold text-gray-300">
                      {group.groupName}
                    </td>
                  </tr>
                  {/* Features within the Group */}
                  {group.features.map((feature, index) => (
                    <tr key={index} className="border-b border-gray-700">
                      <td className="px-6 py-4 text-left text-sm">
                        <div className="flex flex-col">
                          <span className="font-medium text-sm sm:text-base text-gray-300">{feature.name}</span>
                          <span className="text-xs sm:text-sm text-gray-400">{feature.description}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">
                        {feature.free ? (
                          <span className="flex items-center justify-center">
                            <FaCheck className="text-green-500" size={20} aria-label="Available" />
                            {!feature.freeAlways && <span className="text-yellow-400 text-lg font-bold ml-1">*</span>}
                          </span>
                        ) : (
                          <FaTimes className="text-red-500 mx-auto" size={20} aria-label="Not Available" />
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {feature.free ? (
                          <FaCheck className="text-green-500 mx-auto" size={20} aria-label="Available" />
                        ) : (
                          <FaTimes className="text-red-500 mx-auto" size={20} aria-label="Not Available" />
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {feature.pro ? (
                          <FaCheck className="text-green-500 mx-auto" size={20} aria-label="Available" />
                        ) : (
                          <FaTimes className="text-red-500 mx-auto" size={20} aria-label="Not Available" />
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* Add spacing after each group 
                  <tr>
                    <td colSpan={3} className="py-2 border-none"></td>
                  </tr>
                  */}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-sm text-yellow-400 mt-4 text-left">
          <span className="font-bold">*</span> Free for 10 days trial period
        </p>

      </div>
    </div>
  );
};

export default Features;
